import { withLifecycleCallbacks } from 'react-admin';
import { supabaseDataProvider } from 'ra-supabase';
import { supabaseClient, instanceUrl, apiKey } from './components/supabase';
import getWeeksPerTerm from './components/TermFunctions';

const baseDataProvider = supabaseDataProvider({
  instanceUrl: instanceUrl,
  apiKey: apiKey,
  supabaseClient,
});

const ticket_statuses = [
  { id: 'NEW', name: 'NEW' },
  { id: 'PENDING', name: 'PENDING' },
  { id: 'TESTING', name: 'TESTING' },
  { id: 'RESOLVED', name: 'RESOLVED' },
  { id: 'BACKLOG', name: 'BACKLOG' },
];

const findMediaType = (ext) => {
  const mediaTypes = {
    D: ['docx', 'pptx', 'pdf', 'txt', 'doc', 'ppt'],
    P: ['png', 'jpg', 'jpeg', 'bmp'],
    V: ['mov', 'mp4', 'avi', 'mkv'],
  };

  for (const [key, value] of Object.entries(mediaTypes)) {
    if (value.includes(ext)) {
      return key;
    }
  }
  return null;
};

export const dataProvider = withLifecycleCallbacks(
  {
    ...baseDataProvider,
    getList: (resource, params) => {
      if (resource === 'loerieleersentrum') {
        return baseDataProvider.getList('lessons', params);
      }
      return baseDataProvider.getList(resource, params);
    },
    getOne: (resource, params) => {
      if (resource === 'loerieleersentrum') {
        return baseDataProvider.getOne('lessons', params);
      }
      return baseDataProvider.getOne(resource, params);
    },
    getMany: (resource, params) => {
      if (resource === 'loerieleersentrum') {
        return baseDataProvider.getMany('lessons', params);
      }
      return baseDataProvider.getMany(resource, params);
    },
    create: (resource, params) => {
      if (resource === 'loerieleersentrum') {
        return baseDataProvider.create('lessons', params);
      }
      return baseDataProvider.create(resource, params);
    },
    update: (resource, params) => {
      if (resource === 'loerieleersentrum') {
        return baseDataProvider.update('lessons', params);
      }
      return baseDataProvider.update(resource, params);
    },
    delete: (resource, params) => {
      if (resource === 'loerieleersentrum') {
        return baseDataProvider.delete('lessons', params);
      }
      return baseDataProvider.delete(resource, params);
    },
    deleteMany: (resource, params) => {
      if (resource === 'loerieleersentrum') {
        return baseDataProvider.deleteMany('lessons', params);
      }
      return baseDataProvider.deleteMany(resource, params);
    },
  },
  [
    {
      resource: 'lessons',
      // Store attachments temporarily during the create/update cycle
      _pendingAttachments: null,

      beforeGetList: async (params, dataProvider) => {
        // Retrieve user identity from localStorage
        const identityString = localStorage.getItem('identity');
        let userRole = null;

        if (identityString) {
          try {
            const identity = JSON.parse(identityString);
            userRole = identity?.role;
          } catch (error) {
            console.error('Error parsing identity from localStorage', error);
          }
        }

        // Only apply the filter if the user is NOT an admin
        if (userRole !== 'admin') {
          const { data: terms } = await dataProvider.getList('terms', {
            filter: { active: true }, // Only fetch active terms
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'ASC' },
          });

          // Extract active IDs
          const activeTermIds = terms.map((term) => term.id);

          // Apply filter to include only lessons with an active term_id
          return {
            ...params,
            filter: {
              ...params.filter,
              'term_id@in':
                activeTermIds.length === 0
                  ? '()'
                  : `(${activeTermIds.join(',')})`, // Ensuring lessons are from active terms
            },
          };
        }

        // If the user is an admin user
        return params;
      },
      beforeDelete: async (params, dataProvider) => {
        console.log(`+++ FETCHING RELATED LESSON ITEMS +++`);
        const { data: lesson_items, error } = await dataProvider.getList(
          'lesson_items',
          {
            filter: {
              lesson_id: params.id,
            },
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'DESC' },
          }
        );
        if (error)
          console.log(`getManyReference error: ${JSON.stringify(error)}`);
        if (lesson_items.length > 0) {
          console.log(`+++ DELETING +++`);
          console.log(JSON.stringify(lesson_items));
          await dataProvider.deleteMany('lesson_items', {
            ids: lesson_items.map((lesson_item) => lesson_item.id),
          });
        }
        return params;
      },
      beforeCreate: async function (params, dataProvider) {
        console.log(`+++ LESSONS CREATE +++`);
        console.log(JSON.stringify(params));

        // Store attachments in closure variable
        this._pendingAttachments = params.data.attachments ?? null;
        delete params.data.attachments;

        // Toggle the toggle field
        params.data.toggle = !params.data.toggle;

        // Calculate term_week
        const { data: terms } = await supabaseClient.from('terms').select('*');
        const weeksPerTerm = getWeeksPerTerm(terms);
        const weeksList = weeksPerTerm[params.data.term_id];
        const minuend = params.data.week;
        const subtrahend = weeksList[0] - 1;
        const difference = minuend - subtrahend;
        params.data.term_week = difference;

        console.log(`======= beforeCreate return params =======`);
        console.log(
          `Stored ${this._pendingAttachments?.length || 0} attachments`
        );
        console.log(JSON.stringify(params));
        return params;
      },

      afterCreate: async function (record, dataProvider, params) {
        console.log(`+++ LESSONS AFTER CREATE +++`);
        console.log('Record:', JSON.stringify(record));
        console.log(
          'Pending attachments:',
          this._pendingAttachments?.length || 0
        );

        const attachments = this._pendingAttachments;

        if (!attachments || attachments.length === 0) {
          console.log('No attachments to process');
          this._pendingAttachments = null; // Clear
          return record;
        }

        const lesson_items = [];
        for (const attachment of attachments) {
          console.log(`--- UPLOADING ---`);
          console.log(JSON.stringify(attachment));
          const unique_attachment_title = `${Date.now()}$:$${attachment.title}`;

          const { error } = await supabaseClient.storage
            .from('media')
            .upload(unique_attachment_title, attachment.rawFile, {
              contentType: 'auto',
              upsert: true,
            });

          if (error) throw error;

          const regex = new RegExp('[^.]+$');
          const extension = attachment.title.match(regex);
          const mediaType = findMediaType(extension.toLocaleString());

          lesson_items.push({
            title: attachment.title,
            file: unique_attachment_title,
            type: mediaType,
            lesson_id: record.data.id,
          });
        }

        // Insert Lesson Items
        console.log(`... SAVING LESSON ITEMS ...`);
        console.log(JSON.stringify(lesson_items));
        if (lesson_items.length) {
          const { error: dataError } = await supabaseClient
            .from('lesson_items')
            .insert(lesson_items);
          if (dataError) throw dataError;
        }

        this._pendingAttachments = null; // Clear after processing
        return record;
      },

      beforeUpdate: async function (params, dataProvider) {
        console.log(`+++ LESSONS UPDATE +++`);
        console.log(JSON.stringify(params));

        // Store attachments in closure variable
        this._pendingAttachments = params.data.attachments ?? null;
        delete params.data.attachments;

        // Toggle the toggle field
        params.data.toggle = !params.data.toggle;

        // Calculate term_week
        const { data: terms } = await supabaseClient.from('terms').select('*');
        const weeksPerTerm = getWeeksPerTerm(terms);
        const weeksList = weeksPerTerm[params.data.term_id];
        const minuend = params.data.week;
        const subtrahend = weeksList[0] - 1;
        const difference = minuend - subtrahend;
        params.data.term_week = difference;

        console.log(
          `Stored ${
            this._pendingAttachments?.length || 0
          } attachments for update`
        );
        return params;
      },

      afterUpdate: async function (record, dataProvider, params) {
        console.log(`+++ LESSONS AFTER UPDATE +++`);
        console.log(
          'Pending attachments:',
          this._pendingAttachments?.length || 0
        );

        const attachments = this._pendingAttachments;

        if (!attachments || attachments.length === 0) {
          console.log('No new attachments to process');
          this._pendingAttachments = null; // Clear
          return record;
        }

        const lesson_items = [];
        for (const attachment of attachments) {
          console.log(`--- UPLOADING ---`);
          console.log(JSON.stringify(attachment));
          const unique_attachment_title = `${Date.now()}$:$${attachment.title}`;

          const { error } = await supabaseClient.storage
            .from('media')
            .upload(unique_attachment_title, attachment.rawFile, {
              contentType: 'auto',
              upsert: true,
            });

          if (error) throw error;

          const regex = new RegExp('[^.]+$');
          const extension = attachment.title.match(regex);
          const mediaType = findMediaType(extension.toLocaleString());

          lesson_items.push({
            title: attachment.title,
            file: unique_attachment_title,
            type: mediaType,
            lesson_id: record.id,
          });
        }

        // Insert Lesson Items
        console.log(`... SAVING LESSON ITEMS ...`);
        console.log(JSON.stringify(lesson_items));
        if (lesson_items.length) {
          const { error: dataError } = await supabaseClient
            .from('lesson_items')
            .insert(lesson_items);
          if (dataError) throw dataError;
        }

        this._pendingAttachments = null; // Clear after processing
        return record;
      },
    },
    {
      resource: 'loerieleersentrum',
      _pendingAttachments: null,

      // Copy all the same callbacks from lessons
      beforeGetList: async (params, dataProvider) => {
        // Same filter logic as lessons
        const identityString = localStorage.getItem('identity');
        let userRole = null;

        if (identityString) {
          try {
            const identity = JSON.parse(identityString);
            userRole = identity?.role;
          } catch (error) {
            console.error('Error parsing identity from localStorage', error);
          }
        }

        if (userRole !== 'admin') {
          const { data: terms } = await dataProvider.getList('terms', {
            filter: { active: true },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'ASC' },
          });

          const activeTermIds = terms.map((term) => term.id);

          return {
            ...params,
            filter: {
              ...params.filter,
              'term_id@in':
                activeTermIds.length === 0
                  ? '()'
                  : `(${activeTermIds.join(',')})`,
            },
          };
        }

        return params;
      },

      beforeDelete: async (params, dataProvider) => {
        console.log(
          `+++ FETCHING RELATED LESSON ITEMS (loerieleersentrum) +++`
        );
        const { data: lesson_items, error } = await dataProvider.getList(
          'lesson_items',
          {
            filter: {
              lesson_id: params.id,
            },
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'DESC' },
          }
        );
        if (error)
          console.log(`getManyReference error: ${JSON.stringify(error)}`);
        if (lesson_items.length > 0) {
          console.log(`+++ DELETING +++`);
          console.log(JSON.stringify(lesson_items));
          await dataProvider.deleteMany('lesson_items', {
            ids: lesson_items.map((lesson_item) => lesson_item.id),
          });
        }
        return params;
      },

      beforeCreate: async function (params, dataProvider) {
        console.log(`+++ LOERIELEERSENTRUM CREATE +++`);
        console.log(JSON.stringify(params));

        this._pendingAttachments = params.data.attachments ?? null;
        delete params.data.attachments;

        params.data.toggle = !params.data.toggle;

        const { data: terms } = await supabaseClient.from('terms').select('*');
        const weeksPerTerm = getWeeksPerTerm(terms);
        const weeksList = weeksPerTerm[params.data.term_id];
        const minuend = params.data.week;
        const subtrahend = weeksList[0] - 1;
        const difference = minuend - subtrahend;
        params.data.term_week = difference;

        console.log(`======= beforeCreate return params =======`);
        console.log(
          `Stored ${this._pendingAttachments?.length || 0} attachments`
        );
        console.log(JSON.stringify(params));
        return params;
      },

      afterCreate: async function (record, dataProvider, params) {
        console.log(`+++ LOERIELEERSENTRUM AFTER CREATE +++`);
        console.log('Record:', JSON.stringify(record));
        console.log(
          'Pending attachments:',
          this._pendingAttachments?.length || 0
        );

        const attachments = this._pendingAttachments;

        if (!attachments || attachments.length === 0) {
          console.log('No attachments to process');
          this._pendingAttachments = null;
          return record;
        }

        const lesson_items = [];
        for (const attachment of attachments) {
          console.log(`--- UPLOADING ---`);
          console.log(JSON.stringify(attachment));
          const unique_attachment_title = `${Date.now()}$:$${attachment.title}`;

          const { error } = await supabaseClient.storage
            .from('media')
            .upload(unique_attachment_title, attachment.rawFile, {
              contentType: 'auto',
              upsert: true,
            });

          if (error) throw error;

          const regex = new RegExp('[^.]+$');
          const extension = attachment.title.match(regex);
          const mediaType = findMediaType(extension.toLocaleString());

          lesson_items.push({
            title: attachment.title,
            file: unique_attachment_title,
            type: mediaType,
            lesson_id: record.data.id,
          });
        }

        console.log(`... SAVING LESSON ITEMS ...`);
        console.log(JSON.stringify(lesson_items));
        if (lesson_items.length) {
          const { error: dataError } = await supabaseClient
            .from('lesson_items')
            .insert(lesson_items);
          if (dataError) throw dataError;
        }

        this._pendingAttachments = null;
        return record;
      },

      beforeUpdate: async function (params, dataProvider) {
        console.log(`+++ LOERIELEERSENTRUM UPDATE +++`);
        console.log(JSON.stringify(params));

        this._pendingAttachments = params.data.attachments ?? null;
        delete params.data.attachments;

        params.data.toggle = !params.data.toggle;

        const { data: terms } = await supabaseClient.from('terms').select('*');
        const weeksPerTerm = getWeeksPerTerm(terms);
        const weeksList = weeksPerTerm[params.data.term_id];
        const minuend = params.data.week;
        const subtrahend = weeksList[0] - 1;
        const difference = minuend - subtrahend;
        params.data.term_week = difference;

        console.log(
          `Stored ${
            this._pendingAttachments?.length || 0
          } attachments for update`
        );
        return params;
      },

      afterUpdate: async function (record, dataProvider, params) {
        console.log(`+++ LOERIELEERSENTRUM AFTER UPDATE +++`);
        console.log(
          'Pending attachments:',
          this._pendingAttachments?.length || 0
        );

        const attachments = this._pendingAttachments;

        if (!attachments || attachments.length === 0) {
          console.log('No new attachments to process');
          this._pendingAttachments = null;
          return record;
        }

        const lesson_items = [];
        for (const attachment of attachments) {
          console.log(`--- UPLOADING ---`);
          console.log(JSON.stringify(attachment));
          const unique_attachment_title = `${Date.now()}$:$${attachment.title}`;

          const { error } = await supabaseClient.storage
            .from('media')
            .upload(unique_attachment_title, attachment.rawFile, {
              contentType: 'auto',
              upsert: true,
            });

          if (error) throw error;

          const regex = new RegExp('[^.]+$');
          const extension = attachment.title.match(regex);
          const mediaType = findMediaType(extension.toLocaleString());

          lesson_items.push({
            title: attachment.title,
            file: unique_attachment_title,
            type: mediaType,
            lesson_id: record.data.id,
          });
        }

        console.log(`... SAVING LESSON ITEMS ...`);
        console.log(JSON.stringify(lesson_items));
        if (lesson_items.length) {
          const { error: dataError } = await supabaseClient
            .from('lesson_items')
            .insert(lesson_items);
          if (dataError) throw dataError;
        }

        this._pendingAttachments = null;
        return record;
      },
    },
    {
      resource: 'lesson_items',
      beforeSave: async (params, dataProvider) => {
        // I doubt that the dataProvider here is needed.
        console.log(
          `beforeSave method in dataProvider for lesson_items -> params: ${JSON.stringify(
            params
          )}`
        );
        if (params.media !== null) {
          const { error } = await supabaseClient.storage
            .from('media')
            .upload(params.media.title, params.media.rawFile, {
              contentType: 'auto', // or specify the mime type if you know it
              upsert: true,
            });

          if (error) {
            throw error;
          }
          const regex = new RegExp('[^.]+$');
          const extension = params.media.title.match(regex);
          const mediaType = findMediaType(extension.toLocaleString());
          console.log(`The extracted file extension: ${extension}`);
          console.log(`The returned mediaType: ${mediaType}`);
          return {
            id: params.id,
            description: params.description,
            file: params.media.title,
            lesson_id: params.lesson_id,
            title: params.title,
            type: mediaType,
          };
        }
        return {
          id: params.id,
          description: params.description,
          file: '',
          lesson_id: params.lesson_id,
          title: params.title,
          type: '',
        };
      },
    },
    {
      resource: 'resources',
      beforeGetList: async (params, dataProvider) => {
        if (params.filter?.tag) {
          const tag = params.filter.tag;
          const cleanFilter = { ...params.filter };
          delete cleanFilter.tag;
          return {
            ...params,
            filter: {
              ...cleanFilter,
              'tags@cs': `{"${tag}"}`,
            },
          };
        }
        return params;
      },
      beforeSave: async (params, dataProvider) => {
        // I doubt that the dataProvider here is needed.
        console.log(
          `beforeSave method in dataProvider for resources -> params: ${JSON.stringify(
            params
          )}`
        );
        const tags = Array.isArray(params.tags)
          ? params.tags.filter(Boolean)
          : [];
        if (params.media !== null) {
          const { error } = await supabaseClient.storage
            .from('media')
            .upload(params.media.title, params.media.rawFile, {
              contentType: 'auto', // or specify the mime type if you know it
              upsert: true,
            });

          if (error) {
            throw error;
          }
          const regex = new RegExp('[^.]+$');
          const extension = params.media.title.match(regex);
          const mediaType = findMediaType(extension.toLocaleString());
          console.log(`The extracted file extension: ${extension}`);
          console.log(`The returned mediaType: ${mediaType}`);
          return {
            id: params.id,
            resource_type_id: params.resource_type_id,
            description: params.description,
            file: params.media.title,
            title: params.title,
            type: mediaType,
            tags,
          };
        }
        return {
          id: params.id,
          resource_type_id: params.resource_type_id,
          description: params.description,
          file: '',
          title: params.title,
          type: '',
          tags,
        };
      },
    },
    {
      resource: 'discipline',
      beforeSave: async (params, dataProvider) => {
        // I doubt that the dataProvider here is needed.
        console.log(
          `beforeSave method in dataProvider for resources -> params: ${JSON.stringify(
            params
          )}`
        );
        if (params.media !== null) {
          const { error } = await supabaseClient.storage
            .from('media')
            .upload(params.media.title, params.media.rawFile, {
              contentType: 'auto', // or specify the mime type if you know it
              upsert: true,
            });

          if (error) {
            throw error;
          }
          const regex = new RegExp('[^.]+$');
          const extension = params.media.title.match(regex);
          const mediaType = findMediaType(extension.toLocaleString());
          console.log(`The extracted file extension: ${extension}`);
          console.log(`The returned mediaType: ${mediaType}`);
          return {
            id: params.id,
            grade: params.grade,
            description: params.description,
            file: params.media.title,
            title: params.title,
            type: mediaType,
          };
        }
        return {
          id: params.id,
          grade: params.grade,
          description: params.description,
          file: '',
          title: params.title,
          type: '',
        };
      },
    },
    {
      resource: 'parents',
      _pendingFilters: null, // Store custom filters for afterGetList
      _originalPagination: null, // Store original pagination for client-side paging

      beforeGetList: async function (params, dataProvider) {
        // Extract custom filters that aren't real database columns
        const gradeFilter = params.filter?.grade;
        const statusFilter = params.filter?.status;

        // Store filters and original pagination for afterGetList
        this._pendingFilters = { grade: gradeFilter, status: statusFilter };
        this._originalPagination = params.pagination;

        // Remove custom filters from the query (they're not DB columns)
        const cleanFilter = { ...params.filter };
        delete cleanFilter.grade;
        delete cleanFilter.status;

        // If any custom filter is active, fetch all records (we'll filter and paginate client-side)
        // Note: Supabase has a 1000 row limit, but ra-data-postgrest handles pagination internally
        let newPagination = params.pagination;
        if (gradeFilter || (statusFilter && statusFilter !== 'all')) {
          newPagination = { page: 1, perPage: 5000 }; // Fetch all for filtering
        }

        return {
          ...params,
          filter: cleanFilter,
          pagination: newPagination,
        };
      },

      afterGetList: async function (response, dataProvider, resource) {
        let parents = response.data;
        const emails = parents.map((p) => p.email).filter(Boolean);

        // Enrich with auth data
        let userMap = {};
        if (emails.length) {
          const { data: users, error } = await supabaseClient.functions.invoke(
            'getAuthUsers'
          );

          if (!error && users) {
            userMap = Object.fromEntries(users.map((u) => [u.email, u]));

            parents = parents.map((p) => ({
              ...p,
              invited_at: userMap[p.email]?.invited_at ?? null,
              last_sign_in_at: userMap[p.email]?.last_sign_in_at ?? null,
              authid: userMap[p.email]?.id ?? null,
            }));
          }
        }

        // Apply custom filters
        const filters = this._pendingFilters;
        const originalPagination = this._originalPagination;
        this._pendingFilters = null;
        this._originalPagination = null;

        let filteredParents = parents;
        let needsClientPagination = false;

        if (filters) {
          // Grade filter
          if (filters.grade) {
            const { data: students } = await supabaseClient
              .from('students')
              .select('household_hash')
              .eq('grade', filters.grade);

            if (students) {
              const householdsWithGrade = new Set(
                students.map((s) => s.household_hash)
              );
              filteredParents = filteredParents.filter((p) =>
                householdsWithGrade.has(p.household_hash)
              );
            }
            needsClientPagination = true;
          }

          // Status filter
          if (filters.status === 'active') {
            filteredParents = filteredParents.filter(
              (p) => p.last_sign_in_at !== null
            );
            needsClientPagination = true;
          } else if (filters.status === 'inactive') {
            filteredParents = filteredParents.filter(
              (p) => p.last_sign_in_at === null
            );
            needsClientPagination = true;
          }
        }

        // Apply client-side pagination if we filtered
        const totalFiltered = filteredParents.length;
        if (needsClientPagination && originalPagination) {
          const { page, perPage } = originalPagination;
          const start = (page - 1) * perPage;
          const end = start + perPage;
          filteredParents = filteredParents.slice(start, end);
        }

        return {
          ...response,
          data: filteredParents,
          total: needsClientPagination ? totalFiltered : response.total,
        };
      },
      afterGetOne: async (response, dataProvider, resource) => {
        const parent = response.data;
        console.log(`parent: ${JSON.stringify(parent)}`);
        const { data: users, error } = await supabaseClient.functions.invoke(
          'getAuthUsers'
        );
        if (error) {
          console.log('Error fetching auth.users:', error);
          return response;
        }
        const userMap = Object.fromEntries(users.map((u) => [u.email, u]));
        const updatedParent = {
          ...parent,
          authid: userMap[parent.email]?.id ?? null,
          invited_at: userMap[parent.email]?.invited_at ?? null,
          last_sign_in_at: userMap[parent.email]?.last_sign_in_at ?? null,
        };

        console.log(`updatedParent: ${JSON.stringify(updatedParent)}`);

        return {
          ...response,
          data: updatedParent,
        };
      },
    },
    {
      resource: 'teachers',
      afterGetList: async (response, dataProvider, resource) => {
        const teachers = response.data;
        const emails = teachers.map((p) => p.email).filter(Boolean);

        if (!emails.length) return response;

        const { data: users, error } = await supabaseClient.functions.invoke(
          'getAuthUsers'
        );

        if (error) {
          console.log('Error fetching auth.users:', error);
          return response;
        }

        // console.log(`AUTH USERS: ${JSON.stringify(users)}`);

        const userMap = Object.fromEntries(users.map((u) => [u.email, u]));

        const enrichedTeachers = teachers.map((p) => ({
          ...p,
          invited_at: userMap[p.email]?.invited_at ?? null,
          last_sign_in_at: userMap[p.email]?.last_sign_in_at ?? null,
        }));

        return {
          ...response,
          data: enrichedTeachers,
        };
      },
      afterGetOne: async (response, dataProvider, resource) => {
        const teacher = response.data;
        console.log(`parent: ${JSON.stringify(teacher)}`);
        const { data: users, error } = await supabaseClient.functions.invoke(
          'getAuthUsers'
        );
        if (error) {
          console.log('Error fetching auth.users:', error);
          return response;
        }
        const userMap = Object.fromEntries(users.map((u) => [u.email, u]));
        const updatedTeacher = {
          ...teacher,
          authid: userMap[teacher.email]?.id ?? null,
          invited_at: userMap[teacher.email]?.invited_at ?? null,
          last_sign_in_at: userMap[teacher.email]?.last_sign_in_at ?? null,
        };

        console.log(`updatedParent: ${JSON.stringify(updatedTeacher)}`);

        return {
          ...response,
          data: updatedTeacher,
        };
      },
    },
    {
      resource: 'tickets',
      beforeSave: async (params) => {
        console.log(
          `beforeSave method in dataProvider for tickets -> params: ${JSON.stringify(
            params
          )}`
        );
        if (params.attachment !== null && params.attachment !== undefined) {
          console.log(`attachment: ${JSON.stringify(params.attachment)}`);
          const { error } = await supabaseClient.storage
            .from('media')
            .upload(params.attachment.title, params.attachment.rawFile, {
              contentType: 'auto', // or specify the mime type if you know it
              upsert: true,
            });

          if (error) {
            console.log(`Weird error: ${error}`);
            throw error;
          }
          return {
            created_at: params.created_at,
            updated_at: params.updated_at,
            resolved_at: params.resolved_at,
            id: params.id,
            title: params.title,
            description: params.description,
            attachment: params.attachment.title,
          };
        }
        const returnParams = {
          created_at: params.created_at,
          updated_at: params.updated_at,
          resolved_at: params.resolved_at,
          id: params.id,
          title: params.title,
          description: params.description,
          attachment: '',
        };
        console.log(`No attachment params: ${JSON.stringify(returnParams)}`);
        return returnParams;
      },
    },
  ]
);
